import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Button, Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { PlusIcon, PrinterIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon, PencilIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../app/hooks";
import { setCourse, setCourses } from "../../app/userSlice";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import ModalForm from "../../components/ModalForm";
import ModalFrame from "../../components/ModalFrame";
import SimplePagination from "../../components/SimplePagination";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import SearchFilter from "../../components/input/SearchFilter";
import { lookupGrade } from "../lookup/lookupUtil";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";
import AddStudentsForm from "./AddStudentsForm";
import AddExistingTeacherForm from "./AddExistingTeacherForm";
import EditCourseByTeacherForm from "./EditCourseByTeacherForm";
import CourseRoster from "./CourseRoster";
import PrintRosterDocument from "./PrintRosterDocument";
import PrintStudentPasswordsDocument from "./PrintStudentPasswordsDocument";
import {
	ManageCourseQueryModel,
	useGetCourseInfoQuery,
	useLazyManageQuery,
	useAddStudentsMutation,
	ProctorCourseEditModel,
	useEditCourseByProctorMutation,
	ManageCourseWritersViewModel,
	useLazyGetAllStudentsForPrintingQuery,
} from "./manageCourseApiSlice";

export default function ManageCourse() {
	const course = useAppSelector((state) => state.user.course);

	const [view, setView] = useState<string>("table");
	const [queryargs, setQueryargs] = useState<ManageCourseQueryModel>({
		nameFilter: "",
		page: 1,
	});
	const [searchFilter, setSearchFilter] = useState<string>("");
	const [isFiltered, setIsFiltered] = useState(false);
	const [page, setPage] = useState(0);

	const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
	const [isEditCourseInfoDialogOpen, setIsEditCourseInfoDialogOpen] = useState(false);

	const [addStudentCheckedIds, setAddStudentCheckedIds] = useState<number[]>([]);
	const [removeStudentMessage, setRemoveStudentMessage] = useState<string>();
	const [errorMessage, setErrorMessage] = useState("");

	const [callManage, callManageResult] = useLazyManageQuery();
	const manageData = callManageResult.currentData;

	const courseInfoResult = useGetCourseInfoQuery();
	const courseInfo = courseInfoResult.data;

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [addStudents] = useAddStudentsMutation();

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (location.state != null) {
			setIsAddStudentDialogOpen(location.state.openAddStudentDialog ?? false);
			setIsEditCourseInfoDialogOpen(location.state.openCourseInfoDialog ?? false);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		if (!manageData) return;
		if (!manageData.allowChanges) setIsAddStudentDialogOpen(false);
	}, [manageData]);

	function handleSearch() {
		if (searchFilter !== "") {
			setIsFiltered(true);
		} else {
			setIsFiltered(false);
		}
		setPage(0);
		updateQueryArgs(0);
	}

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	function updateQueryArgs(startValue: number) {
		setQueryargs({
			...queryargs,
			page: page + 1,
			nameFilter: searchFilter,
		});
	}

	useEffect(() => {
		updateQueryArgs(page);
	}, [page]);

	useEffect(() => {
		if (searchFilter === "") {
			handleSearch();
		}
	}, [searchFilter]);

	useEffect(() => {
		if (queryargs) {
			callManage(queryargs).catch((error: any) => {
				console.log(error);
			});
		}
	}, [queryargs]);

	function handleAddStudents() {
		setSearchFilter("");
		setRemoveStudentMessage(undefined);
		setIsAddStudentDialogOpen(false);
		addStudents(addStudentCheckedIds)
			.unwrap()
			.then((errors) => {
				let errorMessage = "";
				if (errors && errors.length > 0) {
					errorMessage = `The following students could not be added to the course: `;
					errors.map((error) => {
						errorMessage += `\n${error}`;
					});
					scrollTo(0, 0);
				}

				setErrorMessage(errorMessage);
				setAddStudentCheckedIds([]);
			})
			.catch((error) => {
				setErrorMessage(error.data);
			});
	}

	function updateAddStudentCheckedIds(ids: number[]) {
		setAddStudentCheckedIds(ids);
	}

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Course</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "table", label: "Tables" },
						{ value: "raw", label: "Raw Data" },
					]}
				/>
			</div>
			<p className="mt-4">
				Welcome to the Course page! From this page, you can edit course information and add
				co-teachers. You can add students to your course and view or print your student
				roster
				{!courseInfo?.isClassLinkCourse &&
					!courseInfo?.isCleverCourse &&
					" along with their passwords"}
				. Click the drop-down arrow at the end of a student's row to remove the student from
				your course, view the student's draft history or edit the student's information,
				such as their essay scoring level.
			</p>

			{view == "table" && manageData && courseInfo && (
				<>
					{(!manageData.allowChanges || manageData.showExpirationWarning) && (
						<AlertMessage
							className="mt-4 text-sm"
							type={AlertLevel.Warning}
							message={`The course expires in less than a month. It expires on ${new Date(manageData.expirationDate).toLocaleDateString()}. We recommend that you only make minor changes to the roster at this point.`}
							showIcon={true}
						/>
					)}
					{removeStudentMessage && (
						<AlertMessage
							className="mt-4 text-sm"
							type={AlertLevel.Warning}
							message={removeStudentMessage}
							showIcon={true}
						/>
					)}
					<AlertMessage
						className="mt-4 text-sm"
						type={AlertLevel.Error}
						message={errorMessage}
						showIcon={true}
						onClear={() => setErrorMessage("")}
					/>
					<section className="flex xl:flex-row flex-col-reverse mt-4">
						<section className="w-full xl:w-3/4">
							<h2 className="mt-3">Course Roster</h2>
							<div className="flex flex-col sm:flex-row justify-between mt-2.5">
								<div className="flex gap-3">
									<Button
										id="add-students-button"
										className="btn btn-primary"
										disabled={!manageData.allowChanges}
										onClick={() => setIsAddStudentDialogOpen(true)}>
										<span className="flex items-center">
											<PlusIcon className="size-4 mr-2" /> Add Students
										</span>
									</Button>
									<ModalForm
										open={isAddStudentDialogOpen}
										title="Register Existing Students"
										form={
											<AddStudentsForm
												seatsRemaning={manageData.seatsRemaining}
												limitedSeatsRemaining={
													manageData.showSeatsRemaining
														? manageData.limitedSeatLicenses
														: false
												}
												updateCheckedIdsCallback={
													updateAddStudentCheckedIds
												}
											/>
										}
										confirmLabel="Add Students"
										cancelLabel="Cancel"
										disableConfirm={addStudentCheckedIds.length == 0}
										onConfirm={handleAddStudents}
										onCancel={() => setIsAddStudentDialogOpen(false)}
									/>
									{manageData.writersPage.items.length > 0 && (
										<PrintDropdown
											rosterData={manageData}
											showPasswordPrint={
												!courseInfo.isCleverCourse &&
												!courseInfo.isClassLinkCourse
											}
											searchFilter={searchFilter}
											getAllWriters={
												courseInfo.studentCount >
													manageData.writersPage.pageSize || isFiltered
											}
										/>
									)}
								</div>
								<SearchFilter
									className="sm:pt-0 pt-2"
									hiddenLabel
									htmlFor="searchStudents"
									id="searchStudents"
									name="searchStudents"
									maxLength={125}
									label="Search Students"
									placeHolder="Search Students"
									searchFilter={searchFilter}
									setSearchFilter={setSearchFilter}
									actionButton={{
										label: "Search",
										onClick: () => {
											handleSearch();
										},
									}}
								/>
							</div>
							<CourseRoster
								rosterData={manageData}
								setRemoveStudentMessage={setRemoveStudentMessage}
								setErrorMessage={setErrorMessage}
							/>
							{(courseInfo.studentCount > manageData.writersPage.pageSize ||
								isFiltered) && (
								<SimplePagination
									itemCount={manageData.writersPage.totalCount}
									page={page}
									pageSize={manageData.writersPage.pageSize}
									totalCount={courseInfo.studentCount}
									isFiltered={isFiltered}
									onChange={(page) => handlePageChange(page)}
									className={"border-none"}
								/>
							)}
						</section>
						<div className="pt-6 w-full xl:w-1/4 xl:pl-8 xl:pt-0 print:hidden">
							<CourseCard
								id={course?.id}
								name={course?.name ?? ""}
								courseCode={courseInfo.courseCode}
								grade={lookupGrade(course?.gradeId ?? 0, grades ?? [])?.name ?? ""}
								studentCount={courseInfo.studentCount}
								showSeatsRemaining={manageData.showSeatsRemaining}
								seatsRemaining={manageData.seatsRemaining}
								coteachers={courseInfo.coteachers}
								isEditCourseInfoDialogOpen={isEditCourseInfoDialogOpen}
								setErrorMessage={setErrorMessage}
							/>
						</div>
					</section>
				</>
			)}

			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Roster" result={callManageResult} />
					<RtkqResultViewer heading="Course Information" result={courseInfoResult} />
				</>
			)}
		</section>
	);
}

interface CourseCardProps {
	id?: number;
	name: string;
	courseCode: string;
	grade: string;
	studentCount: number;
	showSeatsRemaining: boolean;
	seatsRemaining: number;
	coteachers: string[];
	isEditCourseInfoDialogOpen: boolean;
	setErrorMessage: (message: string) => void;
}

function CourseCard(props: CourseCardProps) {
	const course = useAppSelector((state) => state.user.course);
	const courses = useAppSelector((state) => state.user.courses);
	const [editModel, setEditModel] = useState<ProctorCourseEditModel>();
	const [disableEditCourseConfirm, setDisableEditCourseConfirm] = useState(false);

	const [isAddCoteacherFormOpen, setIsAddCoteacherFormOpen] = useState(false);
	const [isEditCourseInfoDialogOpen, setIsEditCourseInfoDialogOpen] = useState(
		props.isEditCourseInfoDialogOpen
	);

	const dispatch = useDispatch();

	const [editCourseInfo] = useEditCourseByProctorMutation();

	function updateCourseEditModel(model: ProctorCourseEditModel) {
		setEditModel(model);
	}

	function handleEditCourse() {
		if (editModel) {
			editCourseInfo(editModel)
				.unwrap()
				.then(() => {
					props.setErrorMessage("");
					if (course) {
						const updatedCourse = {
							...course,
							name: editModel.name,
							gradeId: editModel.gradeId,
							showUserAvatars:
								!editModel.disableUserImagesByDistrict &&
								!editModel.disableUserImagesBySchool &&
								!editModel.disableUserImages,
						};

						dispatch(setCourse(updatedCourse));

						if (courses) {
							const updatedCourses = courses.map((c) =>
								c.id === course.id ? updatedCourse : c
							);
							dispatch(setCourses(updatedCourses));
						}
					}
				})
				.catch((error) => {
					props.setErrorMessage(error.data);
				})
				.finally(() => {
					setIsEditCourseInfoDialogOpen(false);
				});
		}
	}

	return (
		<ul role="list" className="mt-4">
			<li className="overflow-hidden rounded-xl border border-gray-200">
				<div className="border-b border-gray-900/5 bg-gray-50 p-6">
					<div className="flex items-start justify-between gap-4">
						<p className="text-lg font-medium leading-6 text-gray-900">{props.name}</p>
						<Button
							id="edit-course-button"
							className="border-2 p-1 rounded-md shadow-sm border-gray-900/20"
							onClick={() => setIsEditCourseInfoDialogOpen(true)}>
							<PencilIcon className="size-6" />
						</Button>
						<ModalForm
							open={isEditCourseInfoDialogOpen}
							title="Edit Course Details"
							form={
								<EditCourseByTeacherForm
									updateCourseModel={updateCourseEditModel}
									disableConfirm={setDisableEditCourseConfirm}
								/>
							}
							confirmLabel="Save"
							disableConfirm={disableEditCourseConfirm}
							onConfirm={handleEditCourse}
							onCancel={() => setIsEditCourseInfoDialogOpen(false)}
						/>
					</div>
					<p className="text-sm text-gray-500 mt-1">Course Code: {props.courseCode}</p>
					<p className="text-sm text-gray-500 mt-1">{props.grade}</p>
				</div>
				<div className="divide-y">
					<div className="leading-6">
						<dl
							className={`px-2 grid ${props.showSeatsRemaining ? "grid-cols-2" : "grid-cols-1"} divide-x divide-gray-900/5`}>
							<div
								className={`px-2 py-4 ${props.showSeatsRemaining ? "flex flex-wrap items-baseline justify-between gap-y-1" : "grid grid-cols-1 xl:grid-cols-2 items-center"}`}>
								<dt className="text-sm font-medium leading-6 text-gray-600">
									Student Count
								</dt>
								<dd
									className={`${props.showSeatsRemaining ? "leading-10" : "xl:pl-4"} w-full flex-none text-2xl font-medium self-end tracking-tight text-gray-900`}>
									<p>{props.studentCount}</p>
								</dd>
							</div>
							{props.showSeatsRemaining && (
								<div className="flex flex-wrap items-baseline justify-between gap-y-1 px-2 py-4">
									<dt className="text-sm font-medium leading-6 text-gray-600">
										Seats Remaining
									</dt>
									<dd className="w-full flex-none text-2xl font-medium leading-10 self-end tracking-tight text-gray-900">
										<p>{props.seatsRemaining}</p>
									</dd>
								</div>
							)}
						</dl>
					</div>
					<dl className="px-2 leading-6">
						<div className="px-2 py-4">
							<p className="text-sm font-medium leading-6 text-gray-600">
								Co-Teacher
							</p>
							{props.coteachers.map((teacher) => {
								return (
									<p key={teacher} className="ml-4 text font-medium">
										{teacher}
									</p>
								);
							})}
							<Button
								id="add-coteacher-button"
								className="btn btn-naked border !shadow-md ml-4 mt-2"
								onClick={() => setIsAddCoteacherFormOpen(true)}>
								<span className="flex items-center">
									<PlusIcon className="size-4 mr-2" /> Add Co-Teacher
								</span>
							</Button>

							<ModalFrame
								open={isAddCoteacherFormOpen}
								title="Add Co-Teacher"
								overflow="overflow-visible"
								onClose={() => setIsAddCoteacherFormOpen(false)}>
								<AddExistingTeacherForm
									courseId={course?.id}
									saveButtonText="Add Co-Teacher"
									setErrorMessage={props.setErrorMessage}
									onClose={() => setIsAddCoteacherFormOpen(false)}
								/>
							</ModalFrame>
						</div>
					</dl>
				</div>
			</li>
		</ul>
	);
}

interface PrintProps {
	rosterData: ManageCourseWritersViewModel;
	showPasswordPrint: boolean;
	searchFilter: string;
	getAllWriters: boolean;
}

function PrintDropdown(props: PrintProps) {
	const printRosterRef = useRef<HTMLDivElement>(null);
	const printRoster = useReactToPrint({ contentRef: printRosterRef });

	const printPasswordsRef = useRef<HTMLDivElement>(null);
	const printPasswords = useReactToPrint({ contentRef: printPasswordsRef });

	const [data, setData] = useState(props.rosterData);
	const [pendingPrint, setPendingPrint] = useState<"roster" | "passwords" | null>(null);

	const [callGetAllPrintableStudents] = useLazyGetAllStudentsForPrintingQuery();

	useEffect(() => {
		setData(props.rosterData);
	}, [props.rosterData]);

	useEffect(() => {
		if (pendingPrint && props.getAllWriters) {
			if (pendingPrint === "roster") {
				printRoster();
			} else if (pendingPrint === "passwords") {
				printPasswords();
			}
		}
	}, [data, pendingPrint]);

	function handlePrintRoster() {
		if (props.getAllWriters) {
			setPendingPrint("roster");
			getPrintableStudents();
		} else {
			printRoster();
		}
	}

	function handlePrintPasswords() {
		if (props.getAllWriters) {
			setPendingPrint("passwords");
			getPrintableStudents();
		} else {
			printPasswords();
		}
	}

	function getPrintableStudents() {
		callGetAllPrintableStudents({ nameFilter: props.searchFilter })
			.unwrap()
			.then((payload) => {
				setData({
					...props.rosterData,
					writersPage: {
						...props.rosterData.writersPage,
						items: payload,
					},
				});
			});
	}

	return (
		<section>
			{props.showPasswordPrint ? (
				<Menu as="div">
					<MenuButton id="print-options" className="btn btn-neutral">
						<div className="flex flex-row justify-center items-center">
							<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
							Print
							<ChevronDownIcon className="size-4 ml-2" aria-hidden="true" />
						</div>
					</MenuButton>
					<Transition
						as={Fragment}
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95">
						<MenuItems
							anchor="bottom start"
							className="absolute z-10 pb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden hover:cursor-pointer">
							<MenuItem key="roster">
								<a
									onClick={handlePrintRoster}
									className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
									<div className="flex items-center">
										<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
										Print Roster
									</div>
								</a>
							</MenuItem>
							<MenuItem key="passwords">
								<a
									onClick={handlePrintPasswords}
									className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
									<div className="flex items-center">
										<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
										Print Passwords
									</div>
								</a>
							</MenuItem>
						</MenuItems>
					</Transition>
				</Menu>
			) : (
				<Button id="print-roster" onClick={handlePrintRoster} className="btn btn-neutral">
					<div className="flex items-center">
						<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
						Print Roster
					</div>
				</Button>
			)}
			<div ref={printPasswordsRef} className="hidden print:block">
				<PrintStudentPasswordsDocument students={data.writersPage.items} />
			</div>
			<div ref={printRosterRef} className="hidden print:block">
				<PrintRosterDocument rosterData={data} />
			</div>
		</section>
	);
}
